import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useAuthStore } from './store/auth.store';

import Navbar from './components/Navbar';
import SkipLink from './components/ui/skip-link';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import { Toaster } from './components/ui/toaster';
import SecretSantaMessageNotifier from './components/SecretSantaMessageNotifier';

// Lazy-loaded components for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const WishlistDetailPage = lazy(() => import('./pages/WishlistDetailPage'));
const FriendsPage = lazy(() => import('./pages/FriendsPage'));
const MessagingPage = lazy(() => import('./pages/MessagingPage'));
const SecretSantaListPage = lazy(() => import('./pages/SecretSantaListPage'));
const SecretSantaCreatePage = lazy(() => import('./pages/SecretSantaCreatePage'));
const SecretSantaDetailPage = lazy(() => import('./pages/SecretSantaDetailPage'));
const SecretSantaJoinPage = lazy(() => import('./pages/SecretSantaJoinPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminInvitePage = lazy(() => import('./pages/AdminInvitePage'));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/AdminAnalyticsPage'));
const AdminAiDebugPage = lazy(() => import('./pages/AdminAiDebugPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage'));
const TailwindTest = lazy(() => import('./components/TailwindTest'));
const ShadcnTest = lazy(() => import('./components/ShadcnTest'));
const AccessibilityTest = lazy(() => import('./components/AccessibilityTest'));

function App() {
  const { token, fetchUser } = useAuthStore();

  useEffect(() => {
    // On app load, if a token exists, try to fetch the user data
    if (token) {
      fetchUser();
    }
  }, [token, fetchUser]);

  return (
    <Router>
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      <Toaster />
      <SecretSantaMessageNotifier />
      <Navbar />
      <main id="main-content" className="main-content">
        <div className="container mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-6">
            <Suspense fallback={
              <div className="flex items-center justify-center min-h-[50vh]">
                <div className="h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            }>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/u/:permalink" element={<PublicProfilePage />} />
                <Route path="/wishlists/:id" element={<WishlistDetailPage />} />
                <Route path="/secret-santa/:permalink" element={<SecretSantaDetailPage />} />
                <Route path="/changelog" element={<ChangelogPage />} />
                <Route path="/tailwind-test" element={<TailwindTest />} />
                <Route path="/shadcn-test" element={<ShadcnTest />} />
                <Route path="/accessibility-test" element={<AccessibilityTest />} />
                <Route element={<ProtectedRoute />}>
                  <Route path="/onboarding" element={<OnboardingPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/friends" element={<FriendsPage />} />
                  <Route path="/messages" element={<MessagingPage />} />
                  <Route path="/messages/:userId" element={<MessagingPage />} />
                  <Route path="/secret-santa" element={<SecretSantaListPage />} />
                  <Route path="/secret-santa/create" element={<SecretSantaCreatePage />} />
                  <Route path="/secret-santa/:permalink/join" element={<SecretSantaJoinPage />} />
                </Route>
                <Route element={<AdminRoute />}>
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/admin/invites" element={<AdminInvitePage />} />
                  <Route path="/admin/settings" element={<AdminSettingsPage />} />
                  <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
                  <Route path="/admin/ai-debug" element={<AdminAiDebugPage />} />
                </Route>
              </Routes>
            </Suspense>
          </div>
        </div>
      </main>
    </Router>
  );
}

export default App;